import React, { FormEvent, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import {
    SpsSpinner,
    SpsForm,
    SpsLabel,
    SpsSelect,
    SpsCheckbox,
    SpsButton,
    usePatchReducer,
    SpsValidators,
    useSpsForm,
} from "@spscommerce/ds-react";
import { AppRouteProps } from "../../App";
import { RedirectService } from "../../utils";
import { ButtonKind, ButtonType, SpinnerSize } from "@spscommerce/ds-shared";
import { useSpsLanguage } from "@spscommerce/i18n";

export type SelectOrgProps = Partial<AppRouteProps> & Pick<AppRouteProps, "redirectService">;
interface SearchParamsInterface {
    state?: string;
    client_id?: string;
    key?: string;
}
interface OrgInterface {
    org_id: string;
    org_name: string;
}
interface FormInterface {
    org: OrgInterface | null;
    setDefault: boolean;
}
export interface StateInterface {
    orgOptions: Array<OrgInterface>;
    selectedOrg: OrgInterface | null;
    searchParams: SearchParamsInterface;
    redirectLinkUrl: string | null;
    env: string;
    devCenter: boolean;
    requestError: boolean;
    disableButton: boolean;
    wrapperClasses: string;
    setDefault: boolean;
}
export function SelectOrg() {
    const { t } = useSpsLanguage();
    let location = useLocation();
    const redirectService = new RedirectService();
    const constants = {
        dev: {
            DEV_CENTER: "95WzMD3pE5V3Fo1FhKh3TWkKKzCpvpG1",
            DEV_CENTER_URL: "https://test.developercenter.spscommerce.com/",
            COMMERCE_PLATFORM: "dToeZ4BgX1ZbkEfZCiSZK5dAo3D4A1dt",
            COMMERCE_PLATFORM_URL: "https://dev.commerce.spscommerce.com/",
        },
        test: {
            DEV_CENTER: "95WzMD3pE5V3Fo1FhKh3TWkKKzCpvpG1",
            DEV_CENTER_URL: "https://test.developercenter.spscommerce.com/",
            COMMERCE_PLATFORM: "5NZgjbbJh8n6Tetjw4zy4ntMiP7OTv1Z",
            COMMERCE_PLATFORM_URL: "https://test.commerce.spscommerce.com/",
        },
        stage: {
            DEV_CENTER: "RQCOLMEURd3q0hv3O8IxAxBFPVHF0qiw",
            DEV_CENTER_URL: "https://developercenter.spscommerce.com/",
            COMMERCE_PLATFORM: "XQ6jjSr7cOW9OJp58Ka7OVn2W0GyfTrA",
            COMMERCE_PLATFORM_URL: "https://stage.commerce.spscommerce.com/",
        },
        prod: {
            DEV_CENTER: "RQCOLMEURd3q0hv3O8IxAxBFPVHF0qiw",
            DEV_CENTER_URL: "https://developercenter.spscommerce.com/",
            COMMERCE_PLATFORM: "XgViZevTMNFIioUlf8VyCWADisj1c6xN",
            COMMERCE_PLATFORM_URL: "https://commerce.spscommerce.com/",
        },
    };
    const [showLoader, setShowLoader] = React.useState(true);
    const [error, setError] = React.useState(false);
    const [state, patchState] = usePatchReducer<StateInterface>({
        orgOptions: [],
        selectedOrg: null,
        searchParams: {},
        redirectLinkUrl: null,
        env: "",
        devCenter: false,
        requestError: false,
        disableButton: true,
        wrapperClasses: "",
        setDefault: false,
    });
    const initValue = {
        org: null,
        setDefault: false,
    };

    const { formValue, formMeta, updateForm } = useSpsForm<FormInterface>(initValue, {
        org: [SpsValidators.required],
    });

    function setEnv() {
        const location = window.location;
        return location.hostname.includes("localhost") || location.hostname.includes("dev.")
            ? "dev"
            : location.hostname.includes("test.")
            ? "test"
            : location.hostname.includes("stage.")
            ? "stage"
            : "prod";
    }

    function getParamsFromHash() {
        const search = location.search;
        const stripOtherJunk = search.replace(/[#/?]+/g, "");
        if (!stripOtherJunk) {
            return;
        }
        const keyValArr = stripOtherJunk.split("&").map((keyVal) => keyVal.split("="));
        const params = Object.fromEntries(keyValArr);
        return params;
    }

    /**
     * fetch available user orgs
     * @param {string} url
     * @param {object} paramsObj
     * @returns {Promise}
     */
    async function getOrgs(url: string, paramsObj: { key: string }) {
        const config = {
            headers: {
                "Content-Type": "application/json",
            },
        };
        const body = { key: paramsObj.key };
        const endpoint = url + "/devcenter-auth0/redirect/v1beta1/login-organizations-cache";
        return axios.post(endpoint, body, config).then(function (orgInfoResponse) {
            return orgInfoResponse.data || Promise.reject({ message: "no data property" });
        });
    }

    function validateSearchParams(searchParams: { client_id: string; key: string; state: string }) {
        const env = setEnv();
        if (!searchParams) {
            console.error("No search parameters found.");
            return false;
        }
        let errorState = false;
        if (searchParams.client_id && searchParams.client_id === constants[env].DEV_CENTER) {
            patchState({
                devCenter: true,
                wrapperClasses: "dev-center",
                redirectLinkUrl: constants[env].DEV_CENTER_URL,
            });
        }
        if (!searchParams.key) {
            errorState = true;
            console.error("No search parameters found.");
        }
        if (!searchParams.state) {
            errorState = true;
            console.error("No state parameter found");
        }
        if (errorState) {
            return false;
        }
        return true;
    }

    function handleSubmit(e: FormEvent) {
        e.preventDefault();
        if (!state.selectedOrg) return;
        const orgId = state.selectedOrg.org_id;
        const setDefault = state.setDefault;
        const appDomain =
            state.env === "local" || state.env === "dev" || state.env === "test"
                ? "https://auth.test.spsapps.net"
                : "https://auth.spscommerce.com";
        const redirectUrl =
            appDomain +
            "/continue?state=" +
            state.searchParams.state +
            "&org_id=" +
            orgId +
            "&save_preference=" +
            setDefault;

        if (window.top) {
            window.top.location.href = redirectUrl;
        } else {
            window.location.replace(redirectUrl);
        }
    }

    function orgSearch(query: string | undefined): Array<any> {
        if (!query) {
            return state.orgOptions;
        }
        return state.orgOptions.filter((org) => {
            return org.org_name.toLowerCase().indexOf(query.toLowerCase()) !== -1;
        });
    }

    useEffect(function () {
        const env = setEnv();
        patchState({
            env: env,
            redirectLinkUrl: constants[env].COMMERCE_PLATFORM_URL,
        });
        const url =
            env === "dev" || env === "test"
                ? "https://api.test.spsapps.net"
                : "https://api.spscommerce.com";
        const searchParams = getParamsFromHash();
        if (!validateSearchParams(searchParams)) {
            setShowLoader(false);
            setError(true);
            return;
        }
        patchState({
            searchParams: searchParams,
        });
        async function fetchOrgs() {
            try {
                const response = await getOrgs(url, searchParams);
                updateForm({ ...initValue, org: response.org_list[0] });
                setShowLoader(false);
                patchState({
                    orgOptions: response.org_list,
                    disableButton: false,
                    selectedOrg: response.org_list[0],
                });
            } catch (e) {
                console.error("org fetch process error", e);
                setShowLoader(false);
                setError(true);
                patchState({
                    requestError: true,
                });
            }
        }
        fetchOrgs();
    }, []);

    function handleRefresh() {
        function backToLogin() {
            redirectService.backToLogin();
        }
    }
    return (
        <div className="auth-box select-org">
            {!state.devCenter ? (
                <div className="auth-logo">
                    <div className="logo"></div>
                </div>
            ) : (
                <div className="auth-logo dev-center">
                    <div className="logo"></div>
                    <div className="dev-center-logo-sub-header" id="for-developers">
                        {t("cpauth.selectOrg.forDevelopers", { defaultValue: "for Developers" })}
                    </div>
                </div>
            )}

            <div className="auth-content">
                {showLoader && (
                    <SpsSpinner
                        size={SpinnerSize.MEDIUM}
                        title="Please wait…"
                        data-testid="select-org-loading-spinner"
                    />
                )}
                {!showLoader && !error && (
                    <div className="form-container">
                        <SpsForm onSubmit={handleSubmit} formMeta={formMeta}>
                            <h1 className="auth-content-header mb-3">
                                {t("cpauth.selectOrg.selectYourOrg", {
                                    defaultValue: "Select your organization",
                                })}
                            </h1>
                            <div className="mb-3">
                                <SpsLabel for={formMeta.fields.org}>
                                    {t("cpauth.selectOrg.availableOrgs", {
                                        defaultValue: "Available Organizations",
                                    })}
                                </SpsLabel>
                                <SpsSelect
                                    tabIndex={1}
                                    id="select-org"
                                    value={formValue.org}
                                    onChange={(e: any) => {
                                        patchState({
                                            selectedOrg: e.target.value,
                                        });
                                    }}
                                    options={orgSearch}
                                    textKey="org_name"
                                    notClearable={true}
                                />
                            </div>
                            <div className="mb-3">
                                <SpsCheckbox
                                    checked={formValue.setDefault}
                                    onChange={(e: any) => {
                                        patchState({
                                            setDefault: !state.setDefault,
                                        });
                                    }}
                                    label={t("cpauth.selectOrg.saveAsDefaultOrg", {
                                        defaultValue: "Save as Default Organization",
                                    })}
                                />
                            </div>
                            <SpsButton
                                className="mr-1 auth-button"
                                kind={ButtonKind.CONFIRM}
                                type={ButtonType.SUBMIT}
                                disabled={!state.selectedOrg?.org_id}
                            >
                                {t("cpauth.selectOrg.continueLogin", {
                                    defaultValue: "Continue Login",
                                })}
                            </SpsButton>
                        </SpsForm>
                    </div>
                )}
                {error && (
                    <div id="error-message" className="sps-select-org--error-message">
                        {state.requestError ? (
                            <div>
                                {t("cpauth.selectOrg.fetchOrgError", {
                                    defaultValue:
                                        "An error occured while attempting to fetch organization data. Please",
                                })}{" "}
                                <SpsButton kind={ButtonKind.LINK} onClick={handleRefresh}>
                                    {t("cpauth.selectOrg.tryAgain", { defaultValue: "try again" })}
                                </SpsButton>
                                .
                            </div>
                        ) : (
                            <div>
                                {t("cpauth.selectOrg.missingDataError", {
                                    defaultValue:
                                        "Your link is missing important data. Please check your link and",
                                })}{" "}
                                <SpsButton kind={ButtonKind.LINK} onClick={handleRefresh}>
                                    {t("cpauth.selectOrg.tryAgain", { defaultValue: "try again" })}
                                </SpsButton>
                                .
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
